import type { UsageSeriesPoint } from './api';

const DAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;

export interface DailyBucket {
  /** Calendar day start (local midnight ms). */
  dayStart: number;
  value: number;
  label: string;
}

function startOfLocalDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function dayLetter(ms: number): string {
  return DAY_LETTERS[new Date(ms).getDay()] ?? 'M';
}

function positiveDelta(p: UsageSeriesPoint): number {
  return p.delta != null && p.delta > 0 ? p.delta : 0;
}

function filterSeries(series: UsageSeriesPoint[], registerCode?: string): UsageSeriesPoint[] {
  if (!registerCode) return series;
  return series.filter((p) => p.register_code === registerCode);
}

/** Sum positive deltas per local calendar day. */
export function bucketDailyDeltas(
  series: UsageSeriesPoint[],
  registerCode?: string,
  dayCount = 7,
  referenceDateMs: number = Date.now(),
): DailyBucket[] {
  const filtered = filterSeries(series, registerCode);
  const buckets: DailyBucket[] = [];

  for (let i = dayCount - 1; i >= 0; i--) {
    const dayStart = startOfLocalDay(referenceDateMs - i * 24 * 60 * 60 * 1000);
    const dayEnd = dayStart + 24 * 60 * 60 * 1000;
    const value = filtered
      .filter((p) => {
        const t = new Date(p.at).getTime();
        return t >= dayStart && t < dayEnd;
      })
      .reduce((sum, p) => sum + positiveDelta(p), 0);
    buckets.push({
      dayStart,
      value: Number(value.toFixed(4)),
      label: dayLetter(dayStart),
    });
  }

  return buckets;
}

/** Total positive consumption over a series window. */
export function sumPositiveDeltas(series: UsageSeriesPoint[], registerCode?: string): number {
  const filtered = filterSeries(series, registerCode);
  return Number(
    filtered.reduce((sum, p) => sum + positiveDelta(p), 0).toFixed(4),
  );
}

/**
 * Split a 14d delta series into prior-week (days 8–14 ago) and current-week (days 1–7 ago) totals.
 */
export function splitWeekTotals(
  series: UsageSeriesPoint[],
  registerCode?: string,
  referenceDateMs: number = Date.now(),
): {
  currentWeek: number;
  priorWeek: number;
} {
  const filtered = filterSeries(series, registerCode);
  const currentStart = startOfLocalDay(referenceDateMs - 6 * 24 * 60 * 60 * 1000);
  const priorStart = startOfLocalDay(referenceDateMs - 13 * 24 * 60 * 60 * 1000);
  const priorEnd = currentStart;

  let currentWeek = 0;
  let priorWeek = 0;

  for (const p of filtered) {
    const t = new Date(p.at).getTime();
    const d = positiveDelta(p);
    if (t >= currentStart) {
      currentWeek += d;
    } else if (t >= priorStart && t < priorEnd) {
      priorWeek += d;
    }
  }

  return {
    currentWeek: Number(currentWeek.toFixed(4)),
    priorWeek: Number(priorWeek.toFixed(4)),
  };
}

/** Signed % change current vs prior week; null when prior week has no baseline. */
export function weekOverWeekDeltaPct(currentWeek: number, priorWeek: number): number | null {
  if (priorWeek <= 0) return null;
  return Number((((currentWeek - priorWeek) / priorWeek) * 100).toFixed(1));
}

export function formatUsageTotal(value: number, unit?: string | null): { amount: string; unit: string } {
  const amount =
    value >= 100
      ? value.toLocaleString(undefined, { maximumFractionDigits: 0 })
      : value.toLocaleString(undefined, { maximumFractionDigits: 1 });
  return { amount, unit: unit?.trim() || '' };
}

export function formatTrendPct(deltaPct: number | null): string {
  if (deltaPct === null) return '—';
  const sign = deltaPct > 0 ? '+' : '';
  return `${sign}${deltaPct}% vs last`;
}
